import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { Alert } from "../../Components/Alert";
import {
  useAuthDispatch,
  useAuthState,
  resendCode,
  requestConfirmSignUp,
  requestAutoSignIn,
} from "../../Context";
import { classNames } from "../../Utils";

const RESEND_DELAY = 60;

const ConfirmUser = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { state } = useLocation();
  const { userEmail } = state || {};

  const dispatch = useAuthDispatch();
  const { register, loading, error, confirmStatus, loggedUser } =
    useAuthState();

  const [email, setemail] = useState(userEmail || "");
  const [code, setcode] = useState("");
  const [timer, setTimer] = useState(0);

  useEffect(() => {
    if (confirmStatus?.nextStep?.signUpStep === "COMPLETE_AUTO_SIGN_IN") {
      requestAutoSignIn(dispatch);
    } else if (confirmStatus?.isSignUpComplete) {
      setTimeout(function () {
        navigate("/login");
      }, 3000);
    }
  }, [confirmStatus, dispatch, navigate]);

  useEffect(() => {
    if (loggedUser?.isSignedIn) {
      setTimeout(function () {
        navigate("/dashboard");
      }, 3000);
    }
  }, [loggedUser, navigate]);

  useEffect(() => {
    const storedEndTime = localStorage.getItem("resendCodeEndTime");
    if (storedEndTime) {
      const currentTime = Math.floor(Date.now() / 1000);
      const remainingTime = storedEndTime - currentTime;

      if (remainingTime > 0) {
        setTimer(remainingTime);
      }
    }
  }, []);

  useEffect(() => {
    let interval;

    if (timer > 0) {
      interval = setInterval(() => {
        setTimer((prevTimer) => prevTimer - 1);
      }, 1000);
    }

    return () => clearInterval(interval);
  }, [timer]);

  const handleEmailChange = (event) => {
    setemail(event.target.value);
  };

  const handleCodeChange = (event) => {
    setcode(event.target.value);
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    requestConfirmSignUp(dispatch, { email, code });
  };

  async function resendConfirmationCode() {
    resendCode(dispatch, { email });
    setTimer(RESEND_DELAY);
    const endTime = Math.floor(Date.now() / 1000) + RESEND_DELAY;
    localStorage.setItem("resendCodeEndTime", endTime);
  }

  function renderForm() {
    if (loggedUser?.nextStep?.signInStep === "DONE") {
      return (
        <div className="bg-white p-8 mt-6 mb-0 space-y-4 rounded-lg shadow-sm text-center">
          <div>
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-6 w-6 text-green-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
            <div className="mt-3 sm:mt-5">
              <h3 className="text-lg leading-6 font-medium text-gray-900">
                {t("Code verification successful")}
              </h3>
              <div className="mt-2">
                <p className="text-sm text-gray-500">
                  {t("You can now access your account.")}
                </p>
              </div>
            </div>
          </div>
          <div className="mt-5 sm:mt-6">
            <a href="/dashboard">
              <button
                type="button"
                className="inline-flex justify-center w-full rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:text-sm"
              >
                {t("Go to Dashboard")}
              </button>
            </a>
          </div>
        </div>
      );
    }

    if (confirmStatus?.nextStep?.signUpStep === "DONE") {
      return (
        <div className="bg-white p-8 mt-6 mb-0 space-y-4 rounded-lg shadow-sm text-center">
          <div>
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-6 w-6 text-green-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
            <div className="mt-3 sm:mt-5">
              <h3 className="text-lg leading-6 font-medium text-gray-900">
                {t("Account confirmation successful")}
              </h3>
              <div className="mt-2">
                <p className="text-sm text-gray-500">
                  {t("You can login into your account now.")}
                </p>
              </div>
            </div>
          </div>
          <div className="mt-5 sm:mt-6">
            <a href="/login">
              <button
                type="button"
                className="inline-flex justify-center w-full rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:text-sm"
              >
                {t("Go to Login")}
              </button>
            </a>
          </div>
        </div>
      );
    }

    return (
      <form
        className="bg-white p-8 mt-6 mb-0 space-y-4 rounded-lg shadow-sm"
        action="#"
        onSubmit={handleSubmit}
      >
        <p className="text-lg font-medium">{t("Confirm your account")}</p>

        {register?.codeDeliveryDetails ? (
          <Alert
            type="success"
            message={`${t("Verification code has been sent using")} ${
              register.codeDeliveryDetails.deliveryMedium
            } ${t("to")} ${register.codeDeliveryDetails.destination}`}
          />
        ) : null}

        {error ? (
          <Alert
            type="error"
            message={error?.message || "Error confirm your account."}
            errors={error?.errors}
          />
        ) : null}

        <div>
          <label htmlFor="email" className="text-sm font-medium">
            {t("Email")}
          </label>

          <div className="relative mt-1">
            <input
              type="email"
              id="email"
              className="w-full p-4 pr-12 text-sm border-gray-200 rounded-lg shadow-sm focus:outline-none focus:ring-0 focus:border-blue-500"
              placeholder={t("Enter email")}
              value={email}
              onChange={handleEmailChange}
              required
            />

            <span className="absolute inset-y-0 inline-flex items-center right-4">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="w-5 h-5 text-gray-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207"
                />
              </svg>
            </span>
          </div>
        </div>

        <div>
          <div className="flex justify-between">
            <label htmlFor="code" className="text-sm font-medium">
              {t("Verification Code")}
            </label>
            <button
              disabled={timer > 0 || !email}
              onClick={() => {
                resendConfirmationCode();
              }}
            >
              <span
                className={classNames(
                  "text-sm",
                  timer > 0 || !email ? "text-gray-300" : "text-blue-600"
                )}
              >
                {t("Send code")}
                {timer > 0 && ` (${timer})`}
              </span>
            </button>
          </div>

          <div className="relative mt-1">
            <input
              type="number"
              id="code"
              className="w-full p-4 pr-12 text-sm border-gray-200 rounded-lg shadow-sm focus:outline-none focus:ring-0 focus:border-blue-500"
              placeholder={t("Enter verification code")}
              value={code}
              onChange={handleCodeChange}
              required
            />

            <span className="absolute inset-y-0 inline-flex items-center right-4">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="w-5 h-5 text-gray-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                />
              </svg>
            </span>
          </div>
        </div>

        {loading ? (
          <button
            type="button"
            className="inline-flex justify-center w-full px-5 py-3 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-500 transition ease-in-out duration-150 disabled:text-white disabled:bg-gray-300 disabled:cursor-not-allowed"
            disabled
          >
            <svg
              className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              ></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
            {t("Processing")}
          </button>
        ) : (
          <button
            type="submit"
            className="inline-flex justify-center w-full px-5 py-3 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-500 transition ease-in-out duration-150 "
          >
            {t("Confirm")}
          </button>
        )}
      </form>
    );
  }

  return <div className="max-w-lg mx-auto">{renderForm()}</div>;
};

export default ConfirmUser;
